import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {DEFAULT_FILTERS} from "@/store/filterStoreUtils";
import type {TPaginated} from "@/store/types";
import {type InfiniteData, type QueryKey, useQuery} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {useEffect, useState} from "react";

const useVibeSearchBroadening = <T>({
  buildSimilarUrl,
  queryKey,
}: {
  buildSimilarUrl: (cutoffValue: number, maxCutoffValue: number) => string;
  queryKey: QueryKey;
}) => {
  const [similarFetchUrl, setSimilarFetchUrl] = useState<string | null>(null);

  const {data: similarData, isFetching: isFetchingSimilar} = useQuery({
    queryKey: ["vibe-similar", similarFetchUrl],
    queryFn: async () => {
      const response = await client.get<TPaginated<T>>(similarFetchUrl!);
      return response.data;
    },
    enabled: !!similarFetchUrl,
  });

  useEffect(() => {
    if (!similarData || !similarFetchUrl) return;

    queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<T>>>>(queryKey, old => {
      if (!old) return old;
      if (old.pageParams.includes(similarFetchUrl)) return old;
      return {
        ...old,
        pageParams: [...old.pageParams, similarFetchUrl],
        pages: [...old.pages, {data: similarData} as AxiosResponse<TPaginated<T>>],
      };
    });

    setSimilarFetchUrl(null);
  }, [similarData]);

  const triggerSimilarFetch = (newCutoff: number, currentCutoff: number) => {
    setSimilarFetchUrl(buildSimilarUrl(newCutoff, currentCutoff));
  };

  return {isFetchingSimilar, triggerSimilarFetch};
};

export const extractCutoffFromPageParams = (pageParams: unknown[]): number => {
  const lastBroadenedUrl = pageParams.findLast(
    (p): p is string => typeof p === "string" && p.includes("cutoff_value="),
  );
  const lastParams = lastBroadenedUrl ? new URLSearchParams(lastBroadenedUrl.split("?")[1]) : null;
  return Number(lastParams?.get("cutoff_value")) || DEFAULT_FILTERS.cutoff_value;
};

export const sumCountsByCutoffGroup = <T>(pageParams: unknown[], pages: {data: TPaginated<T>}[]): number => {
  const seen = new Set<string>();
  let sum = 0;
  pages.forEach((page, i) => {
    const param = pageParams[i];
    const cutoffKey =
      typeof param === "string"
        ? (new URLSearchParams(param.split("?")[1] ?? "").get("cutoff_value") ?? "default")
        : "default";
    if (!seen.has(cutoffKey)) {
      seen.add(cutoffKey);
      sum += page.data.count ?? 0;
    }
  });
  return sum;
};

export default useVibeSearchBroadening;
